// Saving/opening files in a way that also works inside the iOS/Android app. The WebViews ignore
// <a download>, file-saver and window.open(blobUrl), so in the app the file is written to the
// cache and handed to the native share sheet (preview, Save to Files, Print, send by mail...).
// On the web every function does exactly what the call sites did before.
import { saveAs } from "file-saver";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Browser } from "@capacitor/browser";
import { isNative } from "./platform";

const EXTENSIONS = {
  "application/pdf": "pdf",
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/calendar": "ics",
  "text/html": "html",
  "application/xml": "xml",
  "text/xml": "xml",
  "image/png": "png",
  "image/jpeg": "jpg"
};

const fallbackName = blob => {
  const ext = EXTENSIONS[(blob.type || "").split(";")[0]] || "bin";
  return `document.${ext}`;
};

const toBase64 = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

async function shareNative(blob, fileName) {
  const safeName = (fileName || fallbackName(blob)).replace(/[\\/:*?"<>|]/g, "_");
  const { uri } = await Filesystem.writeFile({
    path: safeName,
    data: await toBase64(blob),
    directory: Directory.Cache
  });
  try {
    await Share.share({ title: safeName, files: [uri] });
  } catch (err) {
    // Closing the share sheet rejects; that is not an error for the user.
    if (!/cancel/i.test(err?.message || "")) throw err;
  }
}

/** Download a file (web) / share-sheet it (app). */
export async function saveFile(blob, fileName) {
  if (isNative) return shareNative(blob, fileName);
  saveAs(blob, fileName);
}

/**
 * Show a file (PDF, XML...) in a new tab. Pass `tab` when the caller opened it before an await
 * (browsers block later popups); in the app no tab is opened and the share sheet previews it.
 */
export async function openFile(blob, { tab, fileName } = {}) {
  if (isNative) return shareNative(blob, fileName);
  const url = URL.createObjectURL(blob);
  if (tab) tab.location.href = url;
  else if (!window.open(url, "_blank")) window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Pre-open a tab synchronously in the click handler; null in the app (nothing to pre-open). */
export const openBlankTab = () => (isNative ? null : window.open("", "_blank"));

/** External https page (meeting, bank login...): new tab on the web, in-app browser in the app. */
export async function openExternal(url) {
  if (isNative) return Browser.open({ url });
  window.open(url, "_blank");
}
